/* src/modules/interviews/actions.js */

import {
  addScheduleTypes,
  updateTimeblockTypes,
  removeTimeblockTypes,
  updateScheduleTypes,
  removeScheduleTypes,
  fetchScheduleTypes,
  fetchInterviewLocationsTypes,
  fetchInterviewsTypes,
  confirmInterviewTypes,
  declineInterviewTypes,
  fetchApplicantTypes,
  fetchInterviewQuestionsTypes,
  addInterviewQuestionTypes,
  updateInterviewQuestionTypes,
  removeInterviewQuestionTypes,
  saveAnswersAndRatingsTypes,
  updateApplicantStatusTypes,
  CLEAR_APPLICANT_FIELDS,
} from './action_types';

import {
  ADD_SCHEDULE_MUTATION,
  UPDATE_TIMEBLOCK_MUTATION,
  REMOVE_TIMEBLOCK_MUTATION,
  UPDATE_SCHEDULE_MUTATION,
  REMOVE_SCHEDULE_MUTATION,
  FETCH_SCHEDULE_QUERY,
  FETCH_INTERVIEW_LOCATIONS_QUERY,
  FETCH_INTERVIEWS_QUERY,
  CONFIRM_INTERVIEW_MUTATION,
  DECLINE_INTERVIEW_MUTATION,
  FETCH_INTERVIEW_QUESTIONS_QUERY,
  ADD_INTERVIEW_QUESTION_MUTATION,
  UPDATE_INTERVIEW_QUESTION_MUTATION,
  REMOVE_INTERVIEW_QUESTION_MUTATION,
  SAVE_ANSWERS_AND_RATINGNS_MUTATION,
  UPDATE_APPLICANT_STATUS_MUTATION,
} from './api';
import { FETCH_APPLICANT_QUERY } from '../tutors/api';
import { makeMutation, makeQuery, makeActionCreator } from '../../config/client';

/**
 * @param {Object} schedule - param.
 * @param {Object} schedule.input - ScheduleInput.
 * @param {Object} schedule.input.date - date to put availability.
 * @param {number} schedule.input.date.month - month of date.
 * @param {number} schedule.input.date.day - day of date.
 * @param {number} schedule.input.date.year - year of date.
 * @param {Array<{startTime: Object, endTime: Object}>} schedule.input.timepairs
 * - array of start and end time
 * @returns {function} - function to add schedule.
 */
export const addSchedule = schedule => makeMutation({
  variables: schedule,
})(
  addScheduleTypes,
  ADD_SCHEDULE_MUTATION
);

/**
 * @param {Object} variables - variables to pass into the mutation.
 * @param {Object} variables.input - TimeblockUpdateInput!.
 * @returns {function} - function to update a singel timeblock.
 */
export const updateTimeblock = variables => makeMutation({ variables })(
  updateTimeblockTypes,
  UPDATE_TIMEBLOCK_MUTATION
);

/**
 * @param {Object} variables - variables to pass into the mutation.
 * @param {string} variables.timeblock - timeblock id.
 * @returns {function} - function to remove a timeblock.
 */
export const removeTimeblock = variables => makeMutation({ variables })(
  removeTimeblockTypes,
  REMOVE_TIMEBLOCK_MUTATION
);

/**
 * @param {Object} variables - variables to pass into the mutation.
 * @param {Object} variables.input - TimeblockUpdateInput!
 * @returns {function} - function to update all timeblocks in a schedule.
 */
export const updateSchedule = variables => makeMutation({ variables })(
  updateScheduleTypes,
  UPDATE_SCHEDULE_MUTATION
);

/**
 * @param {Object} variables - variables to pass into the mutation.
 * @param {string} variables.timeblock - timeblock id.
 * @returns {function} - function to remove all timeblocks (and maybe the
 *  schedule itself) in a schedule
 */
export const removeSchedule = variables => makeMutation({ variables })(
  removeScheduleTypes,
  REMOVE_SCHEDULE_MUTATION
);

/**
 * @param {Array<string>} universities - list of university ids.
 * @returns {function} - function to get interview schedule data
 */
export const fetchSchedule = universities => makeQuery(universities)(
  fetchScheduleTypes,
  FETCH_SCHEDULE_QUERY
);

/**
 * @param {Object} input - necessary location input
 * @param {Object} input.input - necessary location input
 * @param {Array<string>} input.input.universities - list of university ids.
 * @param {Array<string>} input.input.types - list of location types. should be ['INTERVIEW']
 * @param {number} input.input.radius - radius in meters.
 * @returns {function} - function to get the array of locations.
 */
export const fetchInterviewLocations = input => makeQuery(input)(
  fetchInterviewLocationsTypes,
  FETCH_INTERVIEW_LOCATIONS_QUERY
);

/**
 * @returns {function} - function to get interviews.
 */
export const fetchInterviews = () => makeQuery()(
  fetchInterviewsTypes,
  FETCH_INTERVIEWS_QUERY
);

/**
 * @param {Object} variables = variables to pass into mutation.
 * @param {string} variables.interview - interview id.
 * @returns {function} - confirm interview function.
 */
export const confirmInterview = variables => makeMutation({ variables })(
  confirmInterviewTypes,
  CONFIRM_INTERVIEW_MUTATION
);

/**
 * @param {Object} variables = variables to pass into mutation.
 * @param {string} variables.interview - interview id.
 * @param {string} variables.reason - reason for declining.
 * @returns {function} - decline interview function.
 */
export const declineInterview = variables => makeMutation({ variables })(
  declineInterviewTypes,
  DECLINE_INTERVIEW_MUTATION
);

/**
 * @param {Object} variables - variables to pass into query
 * @param {string} variables.tutor - tutor id.
 * @returns {function} - function to fetch an applicant.
 */
export const fetchApplicant = variables => makeQuery(variables)(
  fetchApplicantTypes,
  FETCH_APPLICANT_QUERY
);

/**
 * @returns {function} - function to fetch interview questions.
 */
export const fetchInterviewQuestions = () => makeQuery()(
  fetchInterviewQuestionsTypes,
  FETCH_INTERVIEW_QUESTIONS_QUERY
);

/**
 * @param {Object} variables - variables to pass into the mutation.
 * @param {string} variables.input - QuestionInput.
 * @param {string} variables.input.text - content of the question.
 * @returns {function} - function to add an interview question.
 */
export const addInterviewQuestion = variables => makeMutation({ variables })(
  addInterviewQuestionTypes,
  ADD_INTERVIEW_QUESTION_MUTATION
);

/**
 * @param {Object} variables - variables to pass into the mutation.
 * @param {string} variables.question - question id.
 * @param {string} variables.text - the new question.
 * @returns {function} - function to update an interview question.
 */
export const updateInterviewQuestion = variables => makeMutation({ variables })(
  updateInterviewQuestionTypes,
  UPDATE_INTERVIEW_QUESTION_MUTATION
);

/**
 * @param {Object} variables - variables to pass into the mutation.
 * @param {string} variables.question - question id
 * @returns {function} - function to fetch interview questions
 */
export const removeInterviewQuestion = variables => makeMutation({ variables })(
  removeInterviewQuestionTypes,
  REMOVE_INTERVIEW_QUESTION_MUTATION
);

/**
 * @param {Object} variables - variables to pass into the mutation.
 * @param {string} variables.applicant - applicant id.
 * @param {Object[]} variables.input - array of QAndAInputs.
 * @param {string} variables.input[].referenceID - question id.
 * @param {string} variables.input[].question - question in full text.
 * @param {string} variables.input[].answer - answer of the question.
 * @param {Object} variables.ratings - ApplicantRatingsInput.
 * @param {number} variables.ratings.knowledge - knowledge rating.
 * @param {number} variables.ratings.helpfulness - helpfulness rating.
 * @param {number} variables.ratings.organization - organization rating.
 * @returns {function} - function to save answers during tutor interview.
 */
export const saveAnswersAndRatings = variables => makeMutation({ variables })(
  saveAnswersAndRatingsTypes,
  SAVE_ANSWERS_AND_RATINGNS_MUTATION
);

/**
 * @param {Object} variables - variables to pass into the mutation.
 * @param {string} variables.applicant - applicant id.
 * @param {string} variables.status - ApplicantStatus.
 * @param {string} variables.reason - reason to decline.
 * @returns {function} - function to update the applicant status.
 */
export const updateApplicantStatus = variables => makeMutation({ variables })(
  updateApplicantStatusTypes,
  UPDATE_APPLICANT_STATUS_MUTATION
);

export const clearApplicantFields = makeActionCreator(CLEAR_APPLICANT_FIELDS);



// WEBPACK FOOTER //
// ./src/modules/interviews/actions.js